import React, { useRef, useEffect, useState } from "react";
import {
  select,
  scaleLinear,
  scaleTime,
  area,
  line,
  extent,
  bisector,
  pointer,
  axisTop,
  axisLeft,
  zoom,
  curveMonotoneX,
} from "d3";
import useResizeObserver from "./useResizeObserver";
import { DEPTH_BANDS_FT, bandForDepthFt } from "./depthBands";
import "./Chart.css";

const MONO_STROKE = "steelblue";

function toDepthFt(depth, metric) {
  const value = Math.abs(Number(depth));
  return metric ? value * 3.28084 : value;
}

function toChartDepth(depthFt, metric) {
  return metric ? depthFt / 3.28084 : depthFt;
}

function visibleBands(chartYMaxFt) {
  return DEPTH_BANDS_FT.filter((band) => band.minFt < chartYMaxFt).map(
    (band) => ({
      ...band,
      maxFt:
        band.maxFt === Infinity
          ? chartYMaxFt
          : Math.min(band.maxFt, chartYMaxFt),
    })
  );
}

function depthSegments(data, metric) {
  if (data.length === 0) {
    return [];
  }
  const segments = [];
  let currentBand = bandForDepthFt(toDepthFt(data[0].depth, metric));
  let points = [data[0]];

  for (let index = 1; index < data.length; index += 1) {
    const point = data[index];
    const band = bandForDepthFt(toDepthFt(point.depth, metric));
    if (band.color !== currentBand.color) {
      points.push(point);
      segments.push({ band: currentBand, points: [...points] });
      currentBand = band;
      points = [point];
    } else {
      points.push(point);
    }
  }
  segments.push({ band: currentBand, points });
  return segments;
}

function ZoomableLineChart({
  map,
  yMax,
  metric,
  setScrollIndex,
  selected,
  trackPointer,
  usaceColors = true,
  id = "myZoomableLineChart",
}) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const dimensions = useResizeObserver(wrapperRef);
  const { data, max, min } = map;
  const chartYMax = yMax ?? max;
  const chartYMaxFt = metric ? chartYMax * 3.28084 : chartYMax;
  const [currentZoomState, setCurrentZoomState] = useState();

  if (selected?.length > 0) {
    select(svgRef.current).select("#focus").style("display", "none");
  }

  useEffect(() => {
    const svg = select(svgRef.current).on(
      "pointerenter pointermove mousedown",
      (event) => {
        event.preventDefault();
        pointermoved(event);
      }
    );

    const svgContent = svg.select(".content");
    const { width, height } =
      dimensions || wrapperRef.current.getBoundingClientRect();

    const xDomain = extent(data, (d) => d.time);

    const xScale = scaleTime(xDomain, [10, width - 10]);

    const yScale = scaleLinear()
      .domain([chartYMax, 0])
      .range([height - 10, 10]);

    if (currentZoomState) {
      const newXScale = currentZoomState.rescaleX(xScale);
      xScale.domain(newXScale.domain());
    }

    const depthValue = (d) => Math.abs(Number(d.depth));
    const yForDepth = (depth) => yScale(depth);

    svgContent.selectAll(".depth-band").remove();
    svgContent.selectAll(".depth-area-segment").remove();
    svgContent.selectAll(".depth-line-segment").remove();
    svgContent.selectAll(".myLine").remove();

    if (usaceColors) {
      svgContent
        .selectAll(".depth-band")
        .data(visibleBands(chartYMaxFt))
        .join("rect")
        .attr("class", "depth-band")
        .attr("x", 10)
        .attr("width", Math.max(0, width - 20))
        .attr("y", (band) => yForDepth(toChartDepth(band.maxFt, metric)))
        .attr("height", (band) =>
          Math.max(
            0,
            yForDepth(toChartDepth(band.minFt, metric)) -
              yForDepth(toChartDepth(band.maxFt, metric))
          )
        )
        .attr("fill", (band) => band.color)
        .attr("opacity", 0.22);
    }

    const areaGenerator = area()
      .x((d) => xScale(d.time))
      .y0(yForDepth(0))
      .y1((d) => yForDepth(depthValue(d)))
      .curve(curveMonotoneX);

    const lineGenerator = line()
      .x((d) => xScale(d.time))
      .y((d) => yForDepth(depthValue(d)))
      .curve(curveMonotoneX);

    if (usaceColors) {
      const segments = depthSegments(data, metric);
      svgContent
        .selectAll(".depth-area-segment")
        .data(segments)
        .join("path")
        .attr("class", "depth-area-segment")
        .attr("fill", (segment) => segment.band.color)
        .attr("opacity", 0.14)
        .attr("stroke", "none")
        .attr("d", (segment) => areaGenerator(segment.points));

      svgContent
        .selectAll(".depth-line-segment")
        .data(segments)
        .join("path")
        .attr("class", "depth-line-segment")
        .attr("fill", "none")
        .attr("stroke", (segment) => segment.band.color)
        .attr("stroke-width", 1.5)
        .attr("opacity", 0.95)
        .attr("d", (segment) => lineGenerator(segment.points));
    } else {
      svgContent
        .selectAll(".myLine")
        .data([data])
        .join("path")
        .attr("class", "myLine")
        .attr("stroke", MONO_STROKE)
        .attr("fill", MONO_STROKE)
        .attr("opacity", 0.5)
        .attr("d", areaGenerator);
    }

    svgContent
      .selectAll(".myDot")
      .data(data)
      .join("circle")
      .attr("class", "myDot")
      .attr("stroke", (value) =>
        usaceColors
          ? bandForDepthFt(toDepthFt(value.depth, metric)).color
          : MONO_STROKE
      )
      .attr("stroke-width", usaceColors ? 0 : 1)
      .attr("r", usaceColors ? 1.2 : 0.5)
      .attr("fill", (value) =>
        usaceColors
          ? bandForDepthFt(toDepthFt(value.depth, metric)).color
          : MONO_STROKE
      )
      .attr("cx", (value) => xScale(value.time))
      .attr("cy", (value) => yForDepth(depthValue(value)));

    const xAxis = axisTop(xScale);
    svg.select(".x-axis").attr("transform", `translate(-8, 8)`).call(xAxis);

    const yAxis = axisLeft(yScale);
    svg.select(".y-axis").call(yAxis);

    svg
      .node()
      .querySelectorAll("#focus")
      .forEach((e) => e.remove());
    const focus = svg.append("g").attr("id", "focus").style("display", "none");
    const bisect = bisector((d) => d.time).center;
    focus.append("line").attr("id", "focusLineX").attr("class", "focusLine");
    focus.append("line").attr("id", "focusLineY").attr("class", "focusLine");
    focus
      .append("circle")
      .attr("id", "focusCircle")
      .attr("r", 3)
      .attr("class", "circle focusCircle");

    function pointermoved(event) {
      if (
        (event.type === "mousedown" && !trackPointer) ||
        (trackPointer &&
          (event.type === "pointerenter" || event.type === "pointermove"))
      ) {
        const i = bisect(data, xScale.invert(pointer(event)[0]));
        setScrollIndex(i);

        const d = data[i];
        const x = xScale(d.time);
        const y = yForDepth(depthValue(d));
        const focusColor = usaceColors
          ? bandForDepthFt(toDepthFt(d.depth, metric)).color
          : "red";

        focus.style("display", "block");
        focus
          .select("#focusCircle")
          .attr("cx", x)
          .attr("cy", y)
          .attr("fill", focusColor)
          .attr("stroke", usaceColors ? "#ffffff" : MONO_STROKE);
        focus
          .select("#focusLineX")
          .attr("x1", x)
          .attr("y1", 10)
          .attr("x2", x)
          .attr("y2", yForDepth(chartYMax));
        focus
          .select("#focusLineY")
          .attr("x1", xScale(xDomain[0]))
          .attr("y1", y)
          .attr("x2", xScale(xDomain[1]))
          .attr("y2", y);
      }
    }

    const zoomBehavior = zoom()
      .scaleExtent([0.75, 100])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .on("zoom", (event) => {
        const zoomState = event.transform;
        setCurrentZoomState(zoomState);
        focus.style("display", "none");
      });

    svg.call(zoomBehavior);

    // eslint-disable-next-line
  }, [
    currentZoomState,
    data,
    dimensions,
    metric,
    max,
    min,
    chartYMax,
    chartYMaxFt,
    trackPointer,
    usaceColors,
  ]);

  return (
    <React.Fragment>
      <div ref={wrapperRef} style={{ marginBottom: "2rem", marginTop: "2rem" }}>
        <svg
          ref={svgRef}
          style={{
            display: "block",
            overflow: "visible",
            width: "100%",
            height: "400px",
          }}
        >
          <defs>
            <clipPath id={id}>
              <rect x="0" y="0" width="100%" height="100%" />
            </clipPath>
          </defs>
          <g className="content" clipPath={`url(#${id})`}></g>
          <g className="x-axis" />
          <g className="y-axis" />
        </svg>
      </div>
    </React.Fragment>
  );
}

export default ZoomableLineChart;
